import { useMemo, cloneElement, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { splitGamesIntoUnits } from './grid-utils';
import { isSmallerSelector } from '~/components/layout/mediaquery';
import { GridsContainer, Grid, CardContainer } from './grid.styles';
import { jackpotGridEnum, jackpotCardSizes } from '~/components/casino-shared/subnavigation/constants';

type JackpotGridMultiplePoolProps = {
	allowIncompleteUnits?: boolean;
	JackpotCard: ReactElement<{
		size: {
			width: number;
			height: number;
		};
		extraStyles?: Record<string, string>;
	}>;
};

const JackpotGridMultiplePool = <T extends ReactElement<unknown>[]>({
	JackpotCard,
	GameCards,
	allowIncompleteUnits = false
}: { GameCards: T } & JackpotGridMultiplePoolProps) => {
	const isSmaller = useSelector(isSmallerSelector);

	const itemsPerUnit = 4;

	const units = useMemo(
		() => splitGamesIntoUnits(GameCards, itemsPerUnit, allowIncompleteUnits),
		[GameCards, itemsPerUnit, allowIncompleteUnits]
	);

	const cardSizes = useMemo(
		() => ({
			// we have usual game cards for now, large ones may come in the future
			small: {
				width: isSmaller ? '104px' : '162px',
				height: isSmaller ? '104px' : '162px'
			}
		}),
		[isSmaller]
	);

	const jackpotCardSize = jackpotCardSizes[jackpotGridEnum.MULTIPLE_POOL][isSmaller ? 'small' : 'large'];

	const gridSizes = useMemo(
		() => ({
			width: isSmaller ? '220px' : '336px',
			height: isSmaller ? '220px' : '336px'
		}),
		[isSmaller]
	);

	return (
		<GridsContainer>
			{cloneElement(JackpotCard, {
				size: jackpotCardSize,
				extraStyles: {
					marginRight: '12px'
				}
			})}
			{units.map((cards, unitIndex) => (
				<Grid
					// eslint-disable-next-line react/no-array-index-key
					key={`unit-${unitIndex}`}
					width={gridSizes.width}
					height={gridSizes.height}
					extraStyles={{
						gridTemplateColumns: `repeat(auto-fill, ${cardSizes.small.width})`,
						gridTemplateRows: `repeat(2, ${cardSizes.small.height})`,
						gridAutoFlow: 'column'
					}}
				>
					{cards.map(card => (
						<CardContainer key={card.key} size={cardSizes.small}>
							{card}
						</CardContainer>
					))}
				</Grid>
			))}
		</GridsContainer>
	);
};

export default JackpotGridMultiplePool;
